import { Recipe, RecipeIngredient, ValidationError } from '../types/recipe';

/**
 * Validates a recipe object and returns an array of validation errors.
 * Checks title (non-empty after trim, max 150 chars), servings (integer, 1-999),
 * and ingredients array (at least 1 required).
 */
export function validateRecipe(recipe: Partial<Recipe>): ValidationError[] {
  const errors: ValidationError[] = [];

  // Title validation
  if (!recipe.title || recipe.title.trim().length === 0) {
    errors.push({
      field: 'title',
      message: 'Title is required and must contain at least 1 non-whitespace character',
    });
  } else if (recipe.title.length > 150) {
    errors.push({
      field: 'title',
      message: 'Title must be 150 characters or fewer',
    });
  }

  // Servings validation
  if (recipe.servings === undefined || recipe.servings === null) {
    errors.push({
      field: 'servings',
      message: 'Servings is required',
    });
  } else if (!Number.isInteger(recipe.servings)) {
    errors.push({
      field: 'servings',
      message: 'Servings must be a whole number',
    });
  } else if (recipe.servings < 1 || recipe.servings > 999) {
    errors.push({
      field: 'servings',
      message: 'Servings must be between 1 and 999',
    });
  }

  // Ingredients validation
  if (!recipe.ingredients || recipe.ingredients.length === 0) {
    errors.push({
      field: 'ingredients',
      message: 'At least one ingredient is required',
    });
  }

  return errors;
}

/**
 * Validates a single ingredient and returns an array of validation errors.
 * Checks name (1-100 chars, non-empty after trim), amount (0.01-999999.99), unit (non-empty).
 * For infused ingredients: also checks cannabinoidType (required),
 * cannabinoidPercentage (0.01-100), materialWeight (0.01-10000),
 * decarboxylationEfficiency (0.01-1.0), extractionEfficiency (0.01-1.0).
 */
export function validateIngredient(
  ingredient: Partial<RecipeIngredient>,
  index: number
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Name validation
  if (!ingredient.name || ingredient.name.trim().length === 0) {
    errors.push({
      field: 'name',
      message: 'Ingredient name is required',
      ingredientIndex: index,
    });
  } else if (ingredient.name.length > 100) {
    errors.push({
      field: 'name',
      message: 'Ingredient name must be 100 characters or fewer',
      ingredientIndex: index,
    });
  }

  // Amount validation
  if (ingredient.amount === undefined || ingredient.amount === null) {
    errors.push({
      field: 'amount',
      message: 'Amount is required',
      ingredientIndex: index,
    });
  } else if (ingredient.amount < 0.01 || ingredient.amount > 999999.99) {
    errors.push({
      field: 'amount',
      message: 'Amount must be between 0.01 and 999,999.99',
      ingredientIndex: index,
    });
  }

  // Unit validation
  if (!ingredient.unit || ingredient.unit.trim().length === 0) {
    errors.push({
      field: 'unit',
      message: 'Unit is required',
      ingredientIndex: index,
    });
  }

  // Infused ingredient validation
  if (ingredient.isCannabisInfused) {
    // Cannabinoid type required
    if (!ingredient.cannabinoidType || ingredient.cannabinoidType.trim().length === 0) {
      errors.push({
        field: 'cannabinoidType',
        message: 'Cannabinoid type is required for infused ingredients',
        ingredientIndex: index,
      });
    }

    // Cannabinoid percentage: 0.01–100
    if (
      ingredient.cannabinoidPercentage === undefined ||
      ingredient.cannabinoidPercentage === null
    ) {
      errors.push({
        field: 'cannabinoidPercentage',
        message: 'Cannabinoid percentage is required for infused ingredients',
        ingredientIndex: index,
      });
    } else if (
      ingredient.cannabinoidPercentage < 0.01 ||
      ingredient.cannabinoidPercentage > 100
    ) {
      errors.push({
        field: 'cannabinoidPercentage',
        message: 'Cannabinoid percentage must be between 0.01 and 100',
        ingredientIndex: index,
      });
    }

    // Material weight: 0.01–10000
    if (ingredient.materialWeight === undefined || ingredient.materialWeight === null) {
      errors.push({
        field: 'materialWeight',
        message: 'Material weight is required for infused ingredients',
        ingredientIndex: index,
      });
    } else if (ingredient.materialWeight < 0.01 || ingredient.materialWeight > 10000) {
      errors.push({
        field: 'materialWeight',
        message: 'Material weight must be between 0.01 and 10,000 grams',
        ingredientIndex: index,
      });
    }

    // Decarboxylation efficiency: 0.01–1.0 (represents 1–100%)
    if (
      ingredient.decarboxylationEfficiency === undefined ||
      ingredient.decarboxylationEfficiency === null
    ) {
      errors.push({
        field: 'decarboxylationEfficiency',
        message: 'Decarboxylation efficiency is required for infused ingredients',
        ingredientIndex: index,
      });
    } else if (
      ingredient.decarboxylationEfficiency < 0.01 ||
      ingredient.decarboxylationEfficiency > 1.0
    ) {
      errors.push({
        field: 'decarboxylationEfficiency',
        message: 'Decarboxylation efficiency must be between 1% and 100%',
        ingredientIndex: index,
      });
    }

    // Extraction efficiency: 0.01–1.0 (represents 1–100%)
    if (
      ingredient.extractionEfficiency === undefined ||
      ingredient.extractionEfficiency === null
    ) {
      errors.push({
        field: 'extractionEfficiency',
        message: 'Extraction efficiency is required for infused ingredients',
        ingredientIndex: index,
      });
    } else if (
      ingredient.extractionEfficiency < 0.01 ||
      ingredient.extractionEfficiency > 1.0
    ) {
      errors.push({
        field: 'extractionEfficiency',
        message: 'Extraction efficiency must be between 1% and 100%',
        ingredientIndex: index,
      });
    }
  }

  return errors;
}
